import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { IconChart, IconDemo, IconIdea, IconMobile, IconProjects, IconStar, IconWarning, IconWave } from '../lib/icons';
import { useTheme } from '../lib/ThemeContext';
import { isDemoMode, exitDemoMode } from '../lib/demoMode';
import { DEMO_USER, DEMO_PROFILE, DEMO_PROJECTS, DEMO_INVOICES, DEMO_EXPENSES } from '../lib/demoData';

// 大見出し用：短縮表示（¥60万、¥1.2億）
const formatShort = (amount) => {
  if (!amount && amount !== 0) return '¥0';
  const n = Number(amount);
  if (n >= 100000000) return `¥${(n / 100000000).toFixed(1)}億`;
  if (n >= 10000) {
    const man = n / 10000;
    return `¥${man % 1 === 0 ? man : man.toFixed(1)}万`;
  }
  return '¥' + n.toLocaleString('ja-JP');
};

// リスト用：フル表示（¥600,000）
const formatFull = (amount) => {
  if (!amount && amount !== 0) return '¥0';
  return '¥' + Number(amount).toLocaleString('ja-JP');
};

export default function Dashboard() {
  const [stats, setStats] = useState({
    monthIncome: 0,
    monthExpense: 0,
    pendingAmount: 0,
    pendingCount: 0,
    activeProjects: 0,
    uninvoicedCount: 0,
    uninvoicedAmount: 0,
    recentProjects: [],
    recentInvoices: [],
  });
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme();
  const [userName, setUserName] = useState('');
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showIosModal, setShowIosModal] = useState(false);
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  const [coach, setCoach] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachError, setCoachError] = useState('');
  const [coachFetched, setCoachFetched] = useState(false);

  // ===== Totonoee Manager 招待通知 =====
  const [pendingInvites, setPendingInvites] = useState([]);
  // ===== Totonoee Manager 案件依頼通知 =====
  const [pendingRequests, setPendingRequests] = useState([]);
  const [requestDetail, setRequestDetail] = useState(null); // 詳細モーダル

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (isIos) { setShowIosModal(true); return; }
    if (installPrompt) { installPrompt.prompt(); setInstallPrompt(null); }
  };

  useEffect(() => { fetchDashboardData(); }, []);

  // ===== Totonoee Manager 招待通知 =====
  useEffect(() => {
    const fetchPendingInvites = async () => {
      if (isDemoMode()) return;
      const { data: _sess } = await supabase.auth.getSession();
      const user = _sess?.session?.user;
      if (!user) return;
      const { data } = await supabase
        .from('admin_freelancers')
        .select('id, admin_id')
        .eq('freelancer_id', user.id)
        .eq('status', 'pending');
      if (!data?.length) { setPendingInvites([]); return; }
      // 管理者名を個別取得（JOINが不安定なため個別fetch）
      const withNames = await Promise.all(
        data.map(async (invite) => {
          const { data: prof } = await supabase
            .from('profiles')
            .select('name')
            .eq('id', invite.admin_id)
            .single();
          return { ...invite, adminName: prof?.name || '管理者' };
        })
      );
      setPendingInvites(withNames);
    };
    fetchPendingInvites();
  }, []);

  const handleAcceptInvite = async (inviteId) => {
    // 2026-06-03 セキュリティ修正：直接UPDATEは廃止。security definer RPC経由のみ
    const { error } = await supabase.rpc('accept_invite', { p_invite_id: inviteId });
    if (error) { console.error('accept_invite error:', error); alert('承認に失敗しました'); return; }
    setPendingInvites(prev => prev.filter(i => i.id !== inviteId));
  };

  const handleRejectInvite = async (inviteId) => {
    // 2026-06-03 セキュリティ修正：直接UPDATEは廃止。security definer RPC経由のみ
    const { error } = await supabase.rpc('reject_invite', { p_invite_id: inviteId });
    if (error) { console.error('reject_invite error:', error); alert('拒否に失敗しました'); return; }
    setPendingInvites(prev => prev.filter(i => i.id !== inviteId));
  };

  // ===== Totonoee Manager 案件依頼通知 =====
  useEffect(() => {
    const fetchPendingRequests = async () => {
      if (isDemoMode()) return;
      const { data: _sess } = await supabase.auth.getSession();
      const user = _sess?.session?.user;
      if (!user) return;
      const { data } = await supabase
        .from('project_requests')
        .select('id, admin_id, title, description, budget, deadline, work_dates, work_type, daily_rate, hourly_rate, hours_per_day, created_at')
        .eq('freelancer_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      if (!data?.length) { setPendingRequests([]); return; }
      const withNames = await Promise.all(
        data.map(async (req) => {
          const { data: prof } = await supabase.from('profiles').select('name').eq('id', req.admin_id).single();
          return { ...req, adminName: prof?.name || '管理者' };
        })
      );
      setPendingRequests(withNames);
    };
    fetchPendingRequests();
  }, []);

  const handleAcceptRequest = async (requestId) => {
    const req = pendingRequests.find(r => r.id === requestId);
    if (!req) return;

    // 1. project_requests のステータス更新
    const { error } = await supabase.from('project_requests').update({ status: 'accepted' }).eq('id', requestId);
    if (error) { console.error('accept error:', error); return; }

    // 2. ユーザー取得
    const { data: authData } = await supabase.auth.getUser();
    const userId = authData?.user?.id;
    if (!userId) return;

    const workType  = req.work_type || 'project';
    const days      = req.work_dates?.length || 0;
    const today     = new Date().toISOString().split('T')[0];
    const startDate = days > 0 ? req.work_dates[0] : today;

    // タイプ別に amount / hourly_rate / hours_worked / work_days をセット
    let insertData = {
      user_id:    userId,
      title:      req.title,
      status:     '進行中',
      work_type:  workType,
      memo:       req.description || null,
      start_date: startDate,
      request_id: requestId,  // 管理者依頼案件の識別用
    };

    switch (workType) {
      case 'daily':
        // 単価 × 日数 = amount
        insertData.amount       = (req.daily_rate || 0) * (days || 1);
        insertData.hourly_rate  = req.daily_rate || 0; // daily_rateをhourly_rateに流用（日単価として）
        insertData.work_days    = days;
        break;
      case 'hourly':
        // 時給 × 時間/日 × 日数 = amount
        insertData.hourly_rate  = req.hourly_rate || 0;
        insertData.hours_worked = (req.hours_per_day || 0) * (days || 1);
        insertData.work_days    = days;
        insertData.amount       = (req.hourly_rate || 0) * (req.hours_per_day || 0) * (days || 1);
        break;
      case 'monthly':
        // 月額固定
        insertData.amount = req.budget || 0;
        break;
      case 'project':
      default:
        // 成果報酬固定
        insertData.amount = req.budget || 0;
        break;
    }

    const { data: newProject, error: projError } = await supabase
      .from('projects').insert(insertData).select().single();

    // 3. 稼働日（daily / hourly）があれば project_work_dates に登録
    if (!projError && newProject && days > 0 && (workType === 'daily' || workType === 'hourly')) {
      const workDateRows = req.work_dates.map(date => ({
        project_id: newProject.id,
        user_id:    userId,
        date:       date,
        work_hours: workType === 'hourly' ? (req.hours_per_day || null) : null,
        memo:       null,
      }));
      await supabase.from('project_work_dates').insert(workDateRows);
    }

    // 4. 依頼した管理者に対して自動公開（admin_project_visibility に INSERT）
    // → Totonoee Manager の案件一覧に表示されるようにする
    if (!projError && newProject) {
      try {
        // admin_freelancers から admin_id と自分の freelancer_id のペアを取得
        const { data: afRow } = await supabase
          .from('admin_freelancers')
          .select('id')
          .eq('admin_id', req.admin_id)
          .eq('freelancer_id', userId)
          .eq('status', 'active')
          .single();
        if (afRow) {
          await supabase.from('admin_project_visibility').insert({
            admin_freelancer_id: afRow.id,
            project_id: newProject.id,
          });
        }
      } catch(e) {
        console.error('auto-visibility error:', e);
        // 自動公開に失敗しても案件登録は成功しているので続行
      }
    }

    setPendingRequests(prev => prev.filter(r => r.id !== requestId));
  };

  const handleRejectRequest = async (requestId) => {
    const { error } = await supabase.from('project_requests').update({ status: 'rejected' }).eq('id', requestId);
    if (!error) setPendingRequests(prev => prev.filter(r => r.id !== requestId));
  };

  const fetchDashboardData = async () => {
    // ── デモモード ────────────────────────────────────────────
    if (isDemoMode()) {
      const user = DEMO_USER;
      setUserName(DEMO_PROFILE.name);
      const projects  = DEMO_PROJECTS;
      const invoices  = DEMO_INVOICES;
      const expenses  = DEMO_EXPENSES;
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const lastDay  = new Date(now.getFullYear(), now.getMonth()+1, 0).toISOString().split('T')[0];
      const monthExp = expenses.filter(e => e.date >= firstDay && e.date <= lastDay);
      const monthInv = invoices.filter(i => i.issued_at >= firstDay && i.issued_at <= lastDay);
      setStats({
        monthIncome:    monthInv.reduce((s,i)=>s+(i.amount||0),0),
        monthExpense:   monthExp.reduce((s,e)=>s+(e.amount||0),0),
        pendingAmount:  invoices.filter(i=>i.status==='未払い').reduce((s,i)=>s+(i.amount||0),0),
        pendingCount:   invoices.filter(i=>i.status==='未払い').length,
        activeProjects: projects.filter(p=>p.status==='進行中').length,
        uninvoicedCount:projects.filter(p=>p.status==='完了').length,
        uninvoicedAmount:projects.filter(p=>p.status==='完了').reduce((s,p)=>s+(p.amount||0),0),
        recentProjects: projects.slice(0,5),
        recentInvoices: invoices.slice(0,5),
      });
      setLoading(false);
      return;
    }
    // ── 通常モード ────────────────────────────────────────────
    const { data: _sess } = await supabase.auth.getSession(); const user = _sess?.session?.user;
    if (!user) { setLoading(false); return; }

    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    // 直近3ヶ月分の開始日
    const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1).toISOString().split('T')[0];

    const [profileRes, invoiceRes, expenseRes, projectRes, allExpenseRes] = await Promise.all([
      supabase.from('profiles').select('name').eq('id', user.id).single(),
      supabase.from('invoices').select('*').eq('user_id', user.id),
      supabase.from('expenses').select('*').eq('user_id', user.id).gte('date', firstDay).lte('date', lastDay),
      supabase.from('projects').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
      supabase.from('expenses').select('amount, category, date').eq('user_id', user.id).gte('date', threeMonthsAgo),
    ]);

    if (profileRes.data) setUserName(profileRes.data.name || user.email?.split('@')[0] || 'ユーザー');

    const invoices = invoiceRes.data || [];
    const expenses = expenseRes.data || [];
    const projects = projectRes.data || [];
    const allExpenses = allExpenseRes.data || [];

    const monthIncome = invoices
      .filter(i => i.status === '入金済み' && i.issued_date >= firstDay && i.issued_date <= lastDay)
      .reduce((s, i) => s + (i.amount || 0), 0);
    const monthExpense = expenses.reduce((s, e) => s + (e.amount || 0), 0);

    const pendingInvoices = invoices.filter(i =>
      i.status === '未入金' || i.status === '送付済み' || i.status === '入金待ち'
    );
    const pendingAmount = pendingInvoices.reduce((s, i) => s + (i.amount || 0), 0);
    const pendingCount = pendingInvoices.length;
    const activeProjects = projects.filter(p => p.status === '進行中').length;

    const recentInvoices = [...invoices]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 3);

    // 直近3ヶ月トレンド
    const trend = [-2, -1, 0].map(offset => {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      const mFirst = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
      const mLast  = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0];
      const inc = invoices.filter(i => i.status === '入金済み' && i.issued_date >= mFirst && i.issued_date <= mLast).reduce((s, i) => s + (i.amount || 0), 0);
      const exp = allExpenses.filter(e => e.date >= mFirst && e.date <= mLast).reduce((s, e) => s + (e.amount || 0), 0);
      return { month: `${d.getMonth() + 1}月`, income: inc, expense: exp };
    });

    // カテゴリ別経費（今月）
    const catMap = {};
    expenses.forEach(e => {
      const cat = e.category || 'その他';
      catMap[cat] = (catMap[cat] || 0) + (e.amount || 0);
    });
    const expenseCategories = Object.entries(catMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([category, amount]) => ({ category, amount }));

    // 未請求案件（status != invoiced かつ amount > 0）
    const uninvoicedProjects = projects.filter(p => p.status !== '請求済み' && p.status !== 'invoiced' && (p.amount || 0) > 0);
    const uninvoicedCount = uninvoicedProjects.length;
    const uninvoicedAmount = uninvoicedProjects.reduce((s, p) => s + (p.amount || 0), 0);

    setStats({ monthIncome, monthExpense, pendingAmount, pendingCount, activeProjects, uninvoicedCount, uninvoicedAmount, recentProjects: projects.slice(0, 3), recentInvoices, trend, expenseCategories });
    setLoading(false);
  };

  const now = new Date();
  const hour = now.getHours();

  const fetchCoach = async () => {
    if (coachLoading || coachFetched) return;
    setCoachLoading(true);
    setCoachError('');
    try {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData?.user?.id;
      const res = await fetch('/api/coach', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(userId ? { 'X-User-Id': userId } : {}) },
        body: JSON.stringify({
          monthIncome:        stats.monthIncome,
          monthExpense:       stats.monthExpense,
          profit:             stats.monthIncome - stats.monthExpense,
          pendingAmount:      stats.pendingAmount,
          pendingCount:       stats.pendingCount,
          trend:              stats.trend || [],
          expenseCategories:  stats.expenseCategories || [],
        }),
      });
      const data = await res.json();
      if (!res.ok) { setCoachError(data.message || data.error || 'エラーが発生しました'); return; }
      setCoach(data);
      setCoachFetched(true);
    } catch (e) {
      setCoachError('通信エラーが発生しました');
    } finally {
      setCoachLoading(false);
    }
  };
  const greeting = hour < 12 ? 'おはようございます' : hour < 18 ? 'こんにちは' : 'こんばんは';
  const monthStr = `${now.getFullYear()}年${now.getMonth() + 1}月`;

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: 32, height: 32, border: `3px solid ${theme.accent}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  return (
    <div style={{ position:'relative' }}>
    <div style={{ padding: '20px 16px', maxWidth: 720, margin: '0 auto' }}>

      {/* iOSインストールモーダル */}
      {showIosModal && (
        <div onClick={() => setShowIosModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, boxShadow: theme.shadow, borderRadius: '20px 20px 0 0', padding: '24px 24px 40px', width: '100%', maxWidth: 480 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: theme.textPrimary, marginBottom: 16 }}>ホーム画面に追加する方法</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14, color: theme.textSecondary }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 22 }}>① </span>
                <span>Safari下部の <strong style={{ color: theme.textPrimary }}>共有ボタン（□↑）</strong> をタップ</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 22 }}>② </span>
                <span><strong style={{ color: theme.textPrimary }}>「ホーム画面に追加」</strong> を選択</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 22 }}>③ </span>
                <span>右上の <strong style={{ color: theme.textPrimary }}>「追加」</strong> をタップ</span>
              </div>
            </div>
            <button onClick={() => setShowIosModal(false)} style={{ marginTop: 24, width: '100%', padding: '12px', borderRadius: 12, border: 'none', background: theme.primary, color: theme.primaryText, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
              閉じる
            </button>
          </div>
        </div>
      )}

      {/* ===== Totonoee Manager 招待通知バナー ===== */}
      {pendingInvites.length > 0 && pendingInvites.map(invite => (
        <div key={invite.id} style={{
          background: 'linear-gradient(135deg, #1e1b4b, #2e1065)',
          border: '1px solid #4F46E5',
          borderRadius: 12,
          padding: '12px 16px',
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: 13, color: '#e2e8f0' }}>
            📨 <strong style={{ color: '#a5b4fc' }}>{invite.adminName}</strong>さんからTotonoee Managerの管理招待が届いています
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => handleAcceptInvite(invite.id)}
              style={{ background: '#4F46E5', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >承認する</button>
            <button
              onClick={() => handleRejectInvite(invite.id)}
              style={{ background: 'transparent', color: '#94a3b8', border: '1px solid #475569', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}
            >拒否</button>
          </div>
        </div>
      ))}

      {/* ===== Totonoee Manager 案件依頼カード ===== */}
      {pendingRequests.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: '#c4b5fd', fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            📩 案件依頼が届いています（{pendingRequests.length}件）
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {pendingRequests.map(req => (
              <div
                key={req.id}
                onClick={() => setRequestDetail(req)}
                style={{
                  background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
                  border: '1px solid #7c3aed',
                  borderLeft: '3px solid #9333ea',
                  borderRadius: 12,
                  padding: '14px 16px',
                  cursor: 'pointer',
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                <div style={{ fontSize: 11, color: '#c4b5fd', marginBottom: 6 }}>
                  <strong>{req.adminName}</strong>さんからの依頼
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 6 }}>{req.title}</div>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                  {req.budget && (
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#34d399' }}>¥{req.budget.toLocaleString('ja-JP')}</span>
                  )}
                  {req.work_dates?.length > 0 && (
                    <span style={{ fontSize: 12, color: '#a5b4fc' }}>📅 {req.work_dates.length}日</span>
                  )}
                  <span style={{ fontSize: 12, color: '#7c3aed', marginLeft: 'auto', fontWeight: 600 }}>詳細を見る →</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== 案件依頼詳細モーダル ===== */}
      {requestDetail && (
        <div onClick={() => setRequestDetail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#1e1b4b', border: '1px solid #4F46E5', borderRadius: 16, padding: '22px 20px', width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto' }}>
            {/* ヘッダー */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#c4b5fd', fontWeight: 600 }}>📩 案件依頼</div>
              <button onClick={() => setRequestDetail(null)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
            </div>

            <div style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginBottom: 6 }}>{requestDetail.title}</div>
            <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 18 }}>
              <strong style={{ color: '#c4b5fd' }}>{requestDetail.adminName}</strong>さんから届いています
            </div>

            {/* 詳細情報 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 22 }}>
              {requestDetail.description && (
                <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>依頼内容</div>
                  <div style={{ fontSize: 14, color: '#cbd5e1', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{requestDetail.description}</div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {requestDetail.budget && (
                  <div style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: 10, padding: '10px 14px', flex: '1 1 100px' }}>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>予算</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#34d399' }}>¥{requestDetail.budget.toLocaleString('ja-JP')}</div>
                  </div>
                )}
                {requestDetail.work_dates?.length > 0 && (
                  <div style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 10, padding: '10px 14px', flex: '1 1 150px' }}>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>稼働日（{requestDetail.work_dates.length}日）</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {requestDetail.work_dates.map(d => (
                        <span key={d} style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 6, padding: '2px 8px', fontSize: 12, color: '#a5b4fc' }}>{d}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* アクションボタン */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { handleRejectRequest(requestDetail.id); setRequestDetail(null); }}
                style={{ flex: 1, background: 'transparent', color: '#94a3b8', border: '1px solid #475569', borderRadius: 10, padding: '12px', fontSize: 14, cursor: 'pointer', fontWeight: 500 }}
              >断る</button>
              <button
                onClick={() => { handleAcceptRequest(requestDetail.id); setRequestDetail(null); }}
                style={{ flex: 2, background: 'linear-gradient(135deg,#7c3aed,#9333ea)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
              >✓ 受ける</button>
            </div>
          </div>
        </div>
      )}

      {/* デモバナー */}
      {isDemoMode() && (
        <div style={{ background:theme.badgeBg3, border:`1px solid ${theme.demodot}`, borderRadius:10, padding:'10px 16px', marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <span style={{ fontSize:13, color:theme.badgeTxt3, fontWeight:600 }}><IconDemo size={14}/> デモモード</span>
            <span style={{ fontSize:12, color:theme.colorOrange, marginLeft:8 }}>データは保存されません</span>
          </div>
          <button onClick={() => { exitDemoMode(); window.location.href = '/'; }} style={{ background:theme.demodot, border:'none', borderRadius:8, padding:'5px 12px', fontSize:12, color:theme.badgeTxt3, cursor:'pointer' }}>
            ログインする
          </button>
        </div>
      )}

      {/* ヘッダー */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: theme.textPrimary, margin: 0 }}>
            {greeting}、{userName}さん <IconWave size={16}/>
          </h1>
          <p style={{ fontSize: 13, color: theme.textMuted, margin: '4px 0 0' }}>{monthStr}の収支サマリー</p>
        </div>
        <button
          onClick={() => window.location.href = '/projects'}
          style={{ background: theme.primary, color: theme.primaryText, border: 'none', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          ＋ 新規案件
        </button>
      </div>

      {/* ホーム画面に追加バナー */}
      {!isInStandaloneMode && (installPrompt || isIos) && (
        <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, boxShadow: theme.shadow, borderRadius: 14, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <IconMobile size={22}/>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>ホーム画面に追加</div>
              <div style={{ fontSize: 11, color: theme.textMuted }}>アプリとして使えます</div>
            </div>
          </div>
          <button
            onClick={handleInstall}
            style={{ background: theme.primary, color: theme.primaryText, border: 'none', borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
          >
            追加する
          </button>
        </div>
      )}

      {/* 上段KPIカード（2行） */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>

        {/* 今月の収入 */}
        <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, boxShadow: theme.shadow, borderTop: `2px solid ${theme.colorGreen}`, borderRadius: 14, padding: '16px 14px' }}>
          <div style={{ fontSize: 11, color: theme.textSecondary, marginBottom: 10 }}>今月の収入（確定）</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: theme.textPrimary, lineHeight: 1.2, marginBottom: 6, wordBreak: 'keep-all' }}>
            {formatShort(stats.monthIncome)}
          </div>
          <div style={{ fontSize: 11, color: theme.textMuted }}>入金済み請求書</div>
        </div>

        {/* 入金待ち */}
        <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, boxShadow: theme.shadow, borderTop: `2px solid ${theme.colorAmber}`, borderRadius: 14, padding: '16px 14px' }}>
          <div style={{ fontSize: 11, color: theme.textSecondary, marginBottom: 10 }}>入金待ち</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: theme.textPrimary, lineHeight: 1.2, marginBottom: 6, wordBreak: 'keep-all' }}>
            {formatShort(stats.pendingAmount)}
          </div>
          <div style={{ fontSize: 11, color: theme.textMuted }}>請求書 {stats.pendingCount}件</div>
        </div>

        {/* 進行中の案件 */}
        <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, boxShadow: theme.shadow, borderTop: `2px solid ${theme.accent}`, borderRadius: 14, padding: '16px 14px' }}>
          <div style={{ fontSize: 11, color: theme.textSecondary, marginBottom: 10 }}>進行中の案件</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: theme.textPrimary, lineHeight: 1.2, marginBottom: 6 }}>
            {stats.activeProjects}
            <span style={{ fontSize: 13, fontWeight: 400, color: theme.textSecondary, marginLeft: 3 }}>件</span>
          </div>
          <div style={{ fontSize: 11, color: theme.textMuted }}>今月の稼働</div>
        </div>

      </div>

      {/* 未請求KPIカード（横長1枚）- 一時非表示 / 復元: この行から </div> までのコメントを外す */}
      {false && <div
        onClick={() => window.location.href = '/projects?filter=uninvoiced'}
        style={{ background: stats.uninvoicedCount > 0 ? theme.purpleBg : theme.bgCard,
          border: `1px solid ${stats.uninvoicedCount > 0 ? theme.violet : theme.border}`,
          borderTop: `2px solid ${stats.uninvoicedCount > 0 ? theme.colorPurple2 : theme.textWeak}`,
          borderRadius: 14, padding: '14px 18px', marginBottom: 20,
          cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          transition: 'opacity 0.15s' }}
        onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <IconProjects size={40}/>
          <div>
            <div style={{ fontSize: 11, color: theme.textSecondary, marginBottom: 4 }}>未請求の案件</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span style={{ fontSize: 22, fontWeight: 700, color: stats.uninvoicedCount > 0 ? theme.purpleMid : theme.textWeak }}>
                {stats.uninvoicedCount}<span style={{ fontSize: 13, fontWeight: 400, color: theme.textSecondary, marginLeft: 2 }}>件</span>
              </span>
              <span style={{ fontSize: 15, fontWeight: 600, color: stats.uninvoicedCount > 0 ? theme.purpleLight : theme.textWeak }}>
                {formatShort(stats.uninvoicedAmount)}
              </span>
            </div>
          </div>
        </div>
        <div style={{ fontSize: 12, color: theme.violet, fontWeight: 600 }}>
          {stats.uninvoicedCount > 0 ? '案件一覧を見る →' : ''}
        </div>
      </div>}

      {/* 最近の請求書 */}
      <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, boxShadow: theme.shadow, borderRadius: 14, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: `1px solid ${theme.border}` }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>最近の請求書</span>
          <a href="/invoices" style={{ fontSize: 12, color: theme.accent, textDecoration: 'none' }}>全て見る →</a>
        </div>
        {stats.recentInvoices.length === 0 ? (
          <div style={{ padding: '24px 16px', textAlign: 'center', color: theme.textMuted, fontSize: 13 }}>
            請求書がありません
          </div>
        ) : (
          stats.recentInvoices.map(inv => (
            <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #0f172a' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  background: inv.status === '入金済み' ? theme.colorGreen : (inv.status === '未入金' || inv.status === '入金待ち') ? theme.colorAmber : theme.textMuted
                }} />
                <span style={{ fontSize: 13, color: theme.colorLight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {inv.title || inv.invoice_no || '—'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 12, flexShrink: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>{formatFull(inv.amount)}</span>
                <span style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 20,
                  background: inv.status === '入金済み' ? theme.badgeBg6 : (inv.status === '未入金' || inv.status === '入金待ち') ? theme.badgeBg3 : theme.badgeBg1,
                  color: inv.status === '入金済み' ? theme.badgeTxt6 : (inv.status === '未入金' || inv.status === '入金待ち') ? theme.badgeTxt3 : theme.textSecondary
                }}>
                  {inv.status}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* AI収支コーチ */}
      <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, boxShadow: theme.shadow, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, letterSpacing: '0.05em' }}>AI COACH</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>収支コーチ</span>
          </div>
          {!coachFetched && (
            <button onClick={fetchCoach} disabled={coachLoading}
              style={{ background: coachLoading ? theme.badgeBg1 : theme.primary, color: coachLoading ? theme.textMuted : theme.primaryText, border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: coachLoading ? 'default' : 'pointer' }}>
              {coachLoading ? '分析中...' : '分析する'}
            </button>
          )}
          {coachFetched && (
            <button onClick={() => { setCoach(null); setCoachFetched(false); }} style={{ background: 'none', border: 'none', color: theme.textWeak, fontSize: 11, cursor: 'pointer' }}>再生成</button>
          )}
        </div>

        <div style={{ padding: '14px 16px' }}>
          {/* 未取得時 */}
          {!coach && !coachLoading && !coachError && (
            <p style={{ fontSize: 13, color: theme.textMuted, margin: 0, lineHeight: 1.7 }}>
              {stats.pendingAmount > 0
                ? `入金待ちが ${formatShort(stats.pendingAmount)} あります。${stats.pendingCount}件の請求書を確認しましょう。`
                : stats.monthIncome > 0
                ? `今月は ${formatShort(stats.monthIncome)} の収入が確定しています。「分析する」でAIコーチにアドバイスをもらいましょう！`
                : '案件や請求書を追加してデータを蓄積すると、AIが詳しくアドバイスします！'}
            </p>
          )}

          {/* ローディング */}
          {coachLoading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
              <div style={{ width: 18, height: 18, border: `2px solid ${theme.accent}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: theme.textSecondary }}>収支データを分析しています...</span>
            </div>
          )}

          {/* エラー */}
          {coachError && (
            <div style={{ background: theme.dangerBg, border: '1px solid #7f1d1d', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: theme.dangerText }}>{coachError}</div>
          )}

          {/* 結果 */}
          {coach && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* サマリー */}
              <p style={{ fontSize: 13, color: theme.colorLight, margin: 0, lineHeight: 1.8 }}>{coach.summary}</p>

              {/* アクション */}
              {coach.actions?.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: theme.textMuted, fontWeight: 600, marginBottom: 8, letterSpacing: '0.05em' }}><IconIdea size={14}/> アクション提案</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {coach.actions.map((a, i) => {
                      const impactColor = a.impact === 'high' ? { bg: theme.badgeBg6, text: theme.badgeTxt6, label: '効果大' } : a.impact === 'mid' ? { bg: theme.badgeBg3, text: theme.badgeTxt3, label: '効果中' } : { bg: theme.badgeBg1, text: theme.textSecondary, label: '効果小' };
                      return (
                        <div key={i} style={{ background: theme.bgInput, borderRadius: 10, padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                          <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, marginTop: 2, background: impactColor.bg, color: impactColor.text }}>{impactColor.label}</span>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: theme.textPrimary, marginBottom: 3 }}>{a.title}</div>
                            <div style={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.6 }}>{a.why}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* リスク */}
              {coach.risks?.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: theme.textMuted, fontWeight: 600, marginBottom: 6, letterSpacing: '0.05em' }}><IconWarning size={14}/> 注意リスク</div>
                  {coach.risks.map((r, i) => (
                    <div key={i} style={{ fontSize: 12, color: theme.badgeTxt3, paddingLeft: 8, marginBottom: 3, lineHeight: 1.6 }}>• {r}</div>
                  ))}
                </div>
              )}

              {/* 次の問い */}
              {coach.next_questions?.length > 0 && (
                <div style={{ background: theme.bgInput, borderRadius: 10, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: theme.textMuted, fontWeight: 600, marginBottom: 6, letterSpacing: '0.05em' }}><IconIdea size={14}/> 考えてみよう</div>
                  {coach.next_questions.map((q, i) => (
                    <div key={i} style={{ fontSize: 12, color: theme.textSecondary, marginBottom: 3 }}>Q. {q}</div>
                  ))}
                </div>
              )}

              {coach.cached && (
                <div style={{ fontSize: 10, color: theme.textSecondary, textAlign: 'right' }}>今週のキャッシュ済み結果</div>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
    </div>
  );
}
